import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/LanguageContext';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import Avatar from './Avatar';
import agriwareLogo from '../../assets/agriware-logo.svg';
import {
  IconLayoutDashboard,
  IconTractor,
  IconUsers,
  IconClipboardList,
  IconShieldCheck,
  IconBriefcase,
  IconSettings,
  IconCreditCard,
  IconLogout,
  IconShieldX,
  IconChartBar,
} from '@tabler/icons-react';

const NAV_ITEMS = [
  { path: '/dashboard',  key: 'nav.dashboard',  icon: IconLayoutDashboard },
  { path: '/machines',   key: 'nav.machines',   icon: IconTractor          },
  { path: '/staff',      key: 'nav.staff',      icon: IconUsers            },
  { path: '/checks',     key: 'nav.checks',     icon: IconClipboardList    },
  { path: '/safety',     key: 'nav.safety',     icon: IconShieldX          },
  { path: '/operations', key: 'nav.operations', icon: IconBriefcase        },
  { path: '/reports',    key: 'nav.reports',    icon: IconChartBar,        roles: ['org_admin', 'manager'] },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, org, role } = useAuth();
  const { t } = useTranslation();

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const roleLabel = (r) => {
    if (r === 'org_admin') return t('role.admin');
    if (r === 'manager') return t('role.manager');
    return t('role.operator');
  };

  return (
    <div style={{
      width: 200,
      background: '#141B24',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      height: '100vh',
      position: 'sticky',
      top: 0,
    }}>
      {/* Brand */}
      <div style={{ padding: '16px 12px 14px', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
        {/* The lockup rather than a generic icon — matches Workshop, and the
            org's own name belongs with the user at the foot of the sidebar. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <img src={agriwareLogo} alt="Agriware" style={{ width: '85%', maxWidth: 150, height: 'auto', display: 'block' }} />
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', marginTop: 8 }}>
            OPERATIONS
          </div>
          {org?.name && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 8, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {org.name}
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
        {NAV_ITEMS.filter(item => !item.roles || item.roles.includes(role)).map(item => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 13,
                cursor: 'pointer',
                color: active ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
                background: active ? 'rgba(232,97,10,0.12)' : 'transparent',
                marginBottom: 2,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <Icon size={16} stroke={1.5} />
              {t(item.key)}
            </div>
          );
        })}
      </div>

      {/* Bottom */}
      <div style={{ padding: 8, borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
        {role === 'org_admin' && (
          <div
            onClick={() => navigate('/settings')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              color: isActive('/settings') ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
              background: isActive('/settings') ? 'rgba(232,97,10,0.12)' : 'transparent',
              marginBottom: 2,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              if (!isActive('/settings')) {
                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }
            }}
            onMouseLeave={e => {
              if (!isActive('/settings')) {
                e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <IconSettings size={16} stroke={1.5} />
            {t('nav.settings')}
          </div>
        )}

        {role === 'org_admin' && (
          <div
            onClick={() => navigate('/settings/billing')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              color: isActive('/settings/billing') ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
              background: isActive('/settings/billing') ? 'rgba(232,97,10,0.12)' : 'transparent',
              marginBottom: 2,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              if (!isActive('/settings/billing')) {
                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }
            }}
            onMouseLeave={e => {
              if (!isActive('/settings/billing')) {
                e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <IconCreditCard size={16} stroke={1.5} />
            {t('nav.billing')}
          </div>
        )}

        <div
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '8px 10px',
            borderRadius: 6,
            fontSize: 13,
            cursor: 'pointer',
            color: isActive('/profile') ? 'var(--color-brand)' : 'rgba(255,255,255,0.7)',
            background: isActive('/profile') ? 'rgba(232,97,10,0.12)' : 'transparent',
            marginBottom: 6,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            if (!isActive('/profile')) {
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            }
          }}
          onMouseLeave={e => {
            if (!isActive('/profile')) {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          <Avatar name={userProfile?.name} photoURL={userProfile?.photoURL} size="xs" />
          <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {userProfile?.name || 'Profile'}
          </div>
        </div>

        <div
          onClick={handleSignOut}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.35)',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.85)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
        >
          <IconLogout size={14} stroke={1.5} />
          {t('nav.sign_out')}
        </div>
      </div>
    </div>
  );
}